import React, { useState } from 'react';
import { ExpandMoreOutlined, ExpandLessOutlined } from '../../icons';
import { formatDuration } from '../StudyPlanCalendar/StudyPlanCalendar';
import '../../dashboard/MyMocksPage.css';

// Paleta cíclica por matéria — aqui a cor não indica bom/ruim (como no painel de
// desempenho), só diferencia visualmente cada matéria na lista de tempo de estudo.
const TIME_PALETTE = ['#C99A2E', '#2596A6', '#D5487A', '#C2492F', '#7C5CD1', '#3D8BFD', '#0EA5A0', '#B14FCE'];
const getTimeColor = (index) => TIME_PALETTE[index % TIME_PALETTE.length];

// Painel "Tempo de estudo por matéria" — mostra quanto tempo o aluno dedicou a cada
// matéria/assunto, calculado a partir das sessões que ele registrou no Diário de
// Estudos (study_session.duration_minutes), com o mesmo padrão expansível do painel
// "Desempenho por matéria" (SubjectPerformancePanel).
const StudyTimePanel = ({
  data,
  totalMinutes = 0,
  title = 'Tempo de estudo por matéria',
  emptyMessage = 'Ainda não há tempo de estudo registrado no Diário de Estudos.',
}) => {
  const [expandedSubjects, setExpandedSubjects] = useState(() => new Set());

  const toggleSubjectTopics = (materia) => {
    setExpandedSubjects((prev) => {
      const next = new Set(prev);
      if (next.has(materia)) {
        next.delete(materia);
      } else {
        next.add(materia);
      }
      return next;
    });
  };

  const maxMinutos = data.reduce((max, item) => Math.max(max, item.minutos), 0);

  return (
    <div className="stats-panel">
      <div className="stats-panel-header-row">
        {title && <h3>{title}</h3>}
        {totalMinutes > 0 && <span className="stats-panel-total">{formatDuration(totalMinutes)} no total</span>}
      </div>
      {data.length > 0 ? (
        <div className="stat-list stat-list--time">
          {data.map((item, index) => {
            const hasTopicBreakdown = item.topicos.length > 0;
            const isExpanded = expandedSubjects.has(item.materia);
            const share = maxMinutos > 0 ? Math.round((item.minutos / maxMinutos) * 100) : 0;
            const color = getTimeColor(index);
            return (
              <div key={item.materia} className="stat-item stat-item--time">
                <div
                  className={`stat-row-header${hasTopicBreakdown ? ' stat-header--clickable' : ''}`}
                  onClick={hasTopicBreakdown ? () => toggleSubjectTopics(item.materia) : undefined}
                  role={hasTopicBreakdown ? 'button' : undefined}
                  tabIndex={hasTopicBreakdown ? 0 : undefined}
                >
                  <span className="stat-row-toggle">
                    {hasTopicBreakdown && (
                      isExpanded ? (
                        <ExpandLessOutlined style={{ fontSize: 16 }} />
                      ) : (
                        <ExpandMoreOutlined style={{ fontSize: 16 }} />
                      )
                    )}
                  </span>
                  <span className="stat-row-name">{item.materia}</span>
                  <strong className="stat-row-time">{formatDuration(item.minutos)}</strong>
                </div>
                <div className="stat-bar-track stat-bar-track--flush">
                  <div
                    className="stat-bar-fill"
                    style={{ width: `${Math.min(share, 100)}%`, background: color }}
                  />
                </div>

                {hasTopicBreakdown && isExpanded && (
                  <div className="stat-topic-list">
                    {item.topicos.map((topico) => {
                      const topicShare = item.minutos > 0 ? Math.round((topico.minutos / item.minutos) * 100) : 0;
                      return (
                        <div key={topico.topico} className="stat-topic-row stat-topic-row--time">
                          <span className="stat-topic-name">{topico.topico}</span>
                          <div className="stat-topic-bar-track">
                            <div
                              className="stat-bar-fill"
                              style={{ width: `${Math.min(topicShare, 100)}%`, background: color }}
                            />
                          </div>
                          <span className="stat-topic-time">{formatDuration(topico.minutos)}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="empty-state">{emptyMessage}</div>
      )}
    </div>
  );
};

export default StudyTimePanel;
